import { useCallback, useState } from 'react';
import { useNavigate as _useNavigate } from 'react-router';
import QueryString from 'qs';
import { routes, TRoutesKeys, TRoutesValues } from '../../routing/routes';
import { store } from '../redux';
import { loadingActions } from '../redux/features/loading.slice';

const getPath = (newPath: string) => {
  let path = newPath;
  if (newPath.includes('?')) {
    const search = QueryString.parse(window.location.search, {
      ignoreQueryPrefix: true,
    });
    const searchNew = QueryString.parse(newPath.split('?')[1]);
    path = `${newPath.split('?')[0]}?${QueryString.stringify({
      ...search,
      ...searchNew,
    })}`;
  } else {
    path = `${newPath}${window.location.search}`;
    if (path.includes('situation') || path.includes('pendingContractId')) {
      path = path.slice(0, path.lastIndexOf('&'));
    }
  }
  return path;
};

export function useNavigate() {
  const navigate = _useNavigate();

  const customNavigate = useCallback(
    (path: TRoutesValues, params = '', state?: Record<string, any>) => {
      /**
       * - Prevent error page navigation on selected test stages.
       */
      if (path === routes.error) {
        // eslint-disable-next-line no-console
        console.error('Prevented navigation to error page on selected environments for easier debugging.');
        store.dispatch(loadingActions.setLoading(false));
        return;
      }

      navigate(getPath(path + params), { state });
    },
    [navigate]
  );

  /* Navigate to arbitrary url */

  const navigateToUrl = useCallback(
    /**
     *
     * @param url Complete URL or it's fragment.
     */
    (url: string) => {
      return navigate(getPath(url));
    },
    [navigate]
  );

  type NavigateObject = Record<
    Exclude<TRoutesKeys, 'error' | 'form'>,
    (params?: string, state?: Record<string, any>) => void
  > & {
    error: (error?: any, params?: string, state?: Record<string, any>) => void;
  } & {
    form: (step: `/${number}` | number, params?: `/${string}` | `?${string}`, state?: Record<string, any>) => void;
  };

  const [navigateObject] = useState<NavigateObject>(() => {
    /* Navigate to any of predefined Paths */
    const _navigateObject = (Object.entries(routes) as Array<[TRoutesKeys, TRoutesValues]>).reduce(
      (acc, [pathKey, pathValue]) => {
        if (pathKey === 'form') {
          return {
            ...acc,
            [pathKey]: (step, params, state) => {
              let _step = 1;

              if (typeof step === 'string') {
                _step = Number(step.replace('/', ''));
              } else {
                _step = step;
              }

              return customNavigate(pathValue, `/${_step}${params ?? ''}`, state);
            },
          };
        }
        if (pathKey === 'error') {
          return {
            ...acc,
            [pathKey]: (error, params = '', state) => {
              // eslint-disable-next-line no-console
              console.error('Error caught: ', error);
              customNavigate(pathValue, params, state);
            },
          };
        }

        return {
          ...acc,
          [pathKey]: (params = '', state) => customNavigate(pathValue, params, state),
        };
      },
      {} as NavigateObject
    );

    Object.assign(_navigateObject, { url: navigateToUrl });

    return _navigateObject;
  });

  return navigateObject as typeof navigateObject & {
    /**
     * - Navigate by passing complete URL or it's fragment.
     * - It can be used to go to any path within the app, not only external address.
     * - By passing only query parameter, e.g. "?newParam=123", it will be appended to existing ones.
     */
    url: typeof navigateToUrl;
  };
}

export type NavigateType = ReturnType<typeof useNavigate>;
